import React, { createContext, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, Outlet, useMatch } from 'react-router-dom'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import PeopleIcon from '@mui/icons-material/People'
import BarChartIcon from '@mui/icons-material/BarChart'
import SettingsIcon from '@mui/icons-material/Settings'
import AddIcon from '@mui/icons-material/Add'
import { useUserContext } from '../../contexts/UserContext'
import { NewLeadModal } from './NewLeadModal'
import { useIsMobile } from './hooks/useIsMobile'
import './CrmLayout.scss'

// Section tab-nav. Only Pipeline/Clients are built; the rest are shown disabled
// until their pages exist, then flipped to enabled NavLinks.
//
// `adminOnly` hides a tab outright rather than disabling it: a disabled tab reads
// as "coming soon" here, which would misdescribe a section this user will never
// get. Settings is every account-wide control — stages, renewal automation,
// branding, imports — and the backend refuses all of it to a member, so offering
// the tab could only lead to a 403.
const TABS = [
    { label: 'Pipeline', to: '/crm/pipeline', icon: <ViewKanbanIcon />, enabled: true },
    { label: 'People', to: '/crm/people', icon: <PeopleIcon />, enabled: true },
    { label: 'Reports', to: '/crm/reports', icon: <BarChartIcon />, enabled: true },
    { label: 'Settings', to: '/crm/settings', icon: <SettingsIcon />, enabled: true, adminOnly: true },
]

// The tab bar's slot for a page's own controls, so the page needs no title row.
// Null on phones (no room) and outside this layout (tests).
const TopbarSlotContext = createContext(null)

// Renders a page's controls in the tab bar. Without the slot it renders
// `fallback` instead — by default the controls themselves, where they stand.
export const CrmTopbarPortal = ({ children, fallback = children }) => {
    const slot = useContext(TopbarSlotContext)
    return slot ? createPortal(children, slot) : fallback
}

// Whether a page's controls go in the tab bar: false on phones and in tests.
export const useInCrmTopbar = () => useContext(TopbarSlotContext) !== null

// The topbar view as the `?owner=` Pipeline and People send: 'all' (none) or a
// user id. Undefined outside this layout (the attach picker, tests), where the
// stores keep the owner they already have.
const OwnerContext = createContext(undefined)
export const useCrmOwner = () => useContext(OwnerContext)

export const CrmLayout = () => {
    const { profileState, isAccountAdmin } = useUserContext()
    const [newLeadOpen, setNewLeadOpen] = useState(false)
    const isMobile = useIsMobile()
    const [topbarSlot, setTopbarSlot] = useState(null)

    const accountName = profileState?.account_name || 'CRM'
    const userEmail = profileState?.user_email || ''
    // Hiding the tab is presentation only — /crm/settings is guarded on its own
    // route, so typing the URL doesn't get around this.
    const tabs = TABS.filter((t) => !t.adminOnly || isAccountAdmin())

    // An admin picks whose records Pipeline and People show: Everyone ('all')
    // or one user by email. Members, and every other page, keep the static pill.
    // Held here, so it lasts while the CRM's pages swap under this layout and
    // starts over on the admin when the CRM is left or the page reloaded.
    // The profile carries no user id, so the admin is matched on email, as
    // PersonDetailBody does; it is loaded before this route renders.
    const meId = (profileState?.account_users || []).find((u) => u?.email && u.email === userEmail)?.id
    const [view, setView] = useState(meId ?? 'all')
    const onPipeline = useMatch('/crm/pipeline')
    const onPeople = useMatch('/crm/people')
    const showViewSelect = isAccountAdmin() && Boolean(onPipeline || onPeople)
    // A member is always 'all': their scope is already only their own records.
    const owner = isAccountAdmin() ? view : 'all'
    const users = (profileState?.account_users || [])
        .filter((u) => u?.id != null)
        .map((u) => ({ id: u.id, label: u.email || u.username || String(u.id) }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return (
        <div className="crm-layout">
            <div className="crm-topbar">
                <div className="crm-org">
                    <span className="crm-org-name">{accountName}</span>
                    {showViewSelect ? (
                        <select
                            className="crm-view-select"
                            aria-label="Whose records to show"
                            value={String(owner)}
                            onChange={(e) => {
                                const v = e.target.value
                                setView(v === 'all' ? v : Number(v))
                            }}
                        >
                            <option value="all">Everyone</option>
                            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.label}</option>)}
                        </select>
                    ) : userEmail && (
                        <span className="crm-org-pill"><span className="dot" />{userEmail}</span>
                    )}
                </div>
                <nav className="crm-tabs">
                    {tabs.map((t) =>
                        t.enabled ? (
                            <NavLink
                                key={t.label}
                                to={t.to}
                                className={({ isActive }) => `crm-tab${isActive ? ' active' : ''}`}
                            >
                                {t.icon}<span>{t.label}</span>
                            </NavLink>
                        ) : (
                            <span key={t.label} className="crm-tab disabled" title="Coming soon">
                                {t.icon}<span>{t.label}</span>
                            </span>
                        )
                    )}
                </nav>
                <div className="crm-actions">
                    <div className="crm-page-actions" ref={setTopbarSlot} />
                    <button className="crm-new-btn" onClick={() => setNewLeadOpen(true)}>
                        <AddIcon style={{ fontSize: 16 }} /> <span className="crm-new-label">New Lead</span>
                    </button>
                </div>
            </div>

            <div className="crm-body">
                <TopbarSlotContext.Provider value={isMobile ? null : topbarSlot}>
                    <OwnerContext.Provider value={owner}>
                        <Outlet />
                    </OwnerContext.Provider>
                </TopbarSlotContext.Provider>
            </div>

            <NewLeadModal open={newLeadOpen} onClose={() => setNewLeadOpen(false)} />
        </div>
    )
}
